import {
  LIVE_HTML_BLOCK_ID_PATTERN,
  LIVE_HTML_CONTRACT_VERSION,
  liveHtmlEventSchema,
  type LiveHtmlEvent,
} from '@okrapdf/schemas/live-html';
import {
  streamUnstructuredPdf,
  streamUnstructuredPdfEffect,
  UnstructuredParserError,
  type StreamUnstructuredPdfEffectOptions,
  type StreamUnstructuredPdfOptions,
  type UnstructuredBatchResult,
  type UnstructuredElement,
  type UnstructuredStrategy,
} from '@okrapdf/parser-unstructured';
import { Cause, Data, Effect, Fiber, TxSemaphore } from 'effect';
import { FetchHttpClient, HttpClient } from 'effect/unstable/http';
import { escapeHtml, pageScaffoldHtml, unstructuredPageHtmlEffect } from './html';

const BLOCK_ID = new RegExp(LIVE_HTML_BLOCK_ID_PATTERN);

export interface LiveViewSnapshotBlock {
  id: string;
  html: string;
}

export interface LiveViewSnapshot {
  seq: number;
  status: 'idle' | 'running' | 'done' | 'error';
  phase: string;
  blocks: LiveViewSnapshotBlock[];
}

export type LiveViewEventInput =
  | { type: 'progress'; phase: string }
  | { type: 'append'; blockId: string; html: string }
  | { type: 'replace'; target: `#${string}`; html: string }
  | { type: 'done' }
  | { type: 'error'; message: string };

export interface LiveViewSessionOptions {
  title: string;
  pdfBytes: Uint8Array;
  apiKey: string;
  endpoint?: string;
  strategy: UnstructuredStrategy;
  batchPages: number;
  concurrency: number;
  requestTimeoutMs?: number;
  maxPages?: number;
  fileName?: string;
  streamPdf?: typeof streamUnstructuredPdf;
  streamPdfEffect?: typeof streamUnstructuredPdfEffect;
  fetchImpl?: typeof fetch;
  onFailure?: (error: unknown) => void;
}

export interface LiveViewSession {
  readonly id: string;
  readonly title: string;
  snapshot(): LiveViewSnapshot;
  events(after?: number): LiveHtmlEvent[];
  subscribe(listener: (event: LiveHtmlEvent) => void): () => void;
  start(): Promise<void>;
  done(): Promise<void>;
  close(): Promise<void>;
}

export class LiveViewSessionError extends Data.TaggedError('LiveViewSessionError')<{
  readonly operation: 'emit' | 'stream';
  readonly message: string;
}> {}

const sessionErrorCauses = new WeakMap<LiveViewSessionError, unknown>();

function sessionFailure(operation: LiveViewSessionError['operation'], cause: unknown) {
  const error = new LiveViewSessionError({ operation, message: `Live view ${operation} failed` });
  sessionErrorCauses.set(error, cause);
  return error;
}

function rootId(html: string): string | undefined {
  return html.trim().match(/^<[A-Za-z][\w-]*\b[^>]*\bid\s*=\s*["']([^"']+)["']/)?.[1];
}

function pageElements(batch: UnstructuredBatchResult, pageNumber: number): UnstructuredElement[] {
  const exact = batch.elements.filter((element) => element.metadata?.page_number === pageNumber);
  if (exact.length || batch.pageCount !== 1) return exact;
  // Some self-hosted/older endpoints omit page_number. A one-page request is still unambiguous.
  return batch.elements.filter((element) => element.metadata?.page_number === undefined);
}

function safePublicFailure(error: unknown): string {
  const status =
    error && typeof error === 'object' && typeof (error as { status?: unknown }).status === 'number'
      ? (error as { status: number }).status
      : undefined;
  if (status)
    return `Unstructured conversion failed (HTTP ${status}). Check the terminal for details.`;
  if (error instanceof Error && error.name === 'AbortError') return 'Conversion was cancelled.';
  return 'Unstructured conversion failed. Check the terminal for details.';
}

function promiseCompatibleProviderFailure(error: unknown): unknown {
  if (!(error instanceof UnstructuredParserError)) return error;
  if (
    (error.reason === 'validation' || error.reason === 'pdf' || error.reason === 'callback') &&
    error.cause instanceof Error
  ) {
    return error.cause;
  }
  const legacy = new Error(error.message) as Error & { status?: number };
  if (error.reason === 'timeout') legacy.name = 'TimeoutError';
  if (error.status !== undefined) legacy.status = error.status;
  return legacy;
}

export function createLiveViewSession(options: LiveViewSessionOptions): LiveViewSession {
  const id = crypto.randomUUID();
  const controller = new AbortController();
  const listeners = new Set<(event: LiveHtmlEvent) => void>();
  const log: LiveHtmlEvent[] = [];
  let snapshot: LiveViewSnapshot = {
    seq: 0,
    status: 'idle',
    phase: 'Starting',
    blocks: [],
  };
  let startPromise: Promise<void> | undefined;
  let runFiber: Fiber.Fiber<void, LiveViewSessionError> | undefined;
  let closed = false;
  const emitSemaphore = Effect.runSync(TxSemaphore.make(1));

  const emitEffect = (input: LiveViewEventInput): Effect.Effect<void, LiveViewSessionError> =>
    TxSemaphore.withPermit(
      emitSemaphore,
      Effect.try({
        try: () => {
          if (snapshot.status === 'done' || snapshot.status === 'error') return;
          const seq = snapshot.seq + 1;
          let event: LiveHtmlEvent;
          let blocks = snapshot.blocks;
          let status: LiveViewSnapshot['status'] = snapshot.status;
          let phase = snapshot.phase;

          if (input.type === 'append') {
            if (!BLOCK_ID.test(input.blockId) || rootId(input.html) !== input.blockId) {
              throw new Error(`live_html_contract: invalid append root ${input.blockId}`);
            }
            if (blocks.some((block) => block.id === input.blockId)) {
              throw new Error(`live_html_contract: duplicate append root ${input.blockId}`);
            }
            event = {
              version: LIVE_HTML_CONTRACT_VERSION,
              seq,
              type: 'append',
              block_id: input.blockId,
              html: input.html,
            };
            blocks = [...blocks, { id: input.blockId, html: input.html }];
          } else if (input.type === 'replace') {
            const idToReplace = input.target.slice(1);
            if (!BLOCK_ID.test(idToReplace) || rootId(input.html) !== idToReplace) {
              throw new Error(`live_html_contract: invalid replace root ${input.target}`);
            }
            if (!blocks.some((block) => block.id === idToReplace)) {
              throw new Error(`live_html_contract: missing replace root ${input.target}`);
            }
            event = {
              version: LIVE_HTML_CONTRACT_VERSION,
              seq,
              type: 'replace',
              target: input.target,
              html: input.html,
            };
            blocks = blocks.map((block) =>
              block.id === idToReplace ? { id: block.id, html: input.html } : block,
            );
          } else if (input.type === 'progress') {
            event = {
              version: LIVE_HTML_CONTRACT_VERSION,
              seq,
              type: 'progress',
              phase: input.phase,
            };
            phase = input.phase;
          } else if (input.type === 'done') {
            event = { version: LIVE_HTML_CONTRACT_VERSION, seq, type: 'done' };
            status = 'done';
            phase = 'Ready';
          } else {
            event = {
              version: LIVE_HTML_CONTRACT_VERSION,
              seq,
              type: 'error',
              message: input.message,
            };
            status = 'error';
            phase = 'Failed';
            const errorBlock = {
              id: 'okra-error',
              html: `<p id="okra-error" role="alert">${escapeHtml(input.message)}</p>`,
            };
            blocks = blocks.some((block) => block.id === errorBlock.id)
              ? blocks.map((block) => (block.id === errorBlock.id ? errorBlock : block))
              : [...blocks, errorBlock];
          }

          liveHtmlEventSchema.parse(event);
          snapshot = { seq, status, phase, blocks };
          log.push(event);
          for (const listener of listeners) {
            try {
              listener(event);
            } catch {
              // A disconnected HTTP subscriber cannot fail or reorder the producer.
            }
          }
        },
        catch: (cause) => sessionFailure('emit', cause),
      }),
    );

  const streamOptions: StreamUnstructuredPdfEffectOptions = {
    pdfBytes: options.pdfBytes,
    apiKey: options.apiKey,
    endpoint: options.endpoint,
    strategy: options.strategy,
    batchPages: options.batchPages,
    concurrency: options.concurrency,
    requestTimeoutMs: options.requestTimeoutMs,
    maxPages: options.maxPages,
    fileName: options.fileName,
    onPageCount: (pageCount) =>
      Effect.runPromise(
        Effect.gen(function* () {
          yield* emitEffect({
            type: 'progress',
            phase: `Sending ${pageCount} page${pageCount === 1 ? '' : 's'} to Unstructured`,
          });
          for (let page = 1; page <= pageCount; page += 1) {
            yield* emitEffect({
              type: 'append',
              blockId: `page-${page}`,
              html: pageScaffoldHtml(page),
            });
          }
        }),
      ),
    onBatch: (batch) =>
      Effect.runPromise(
        Effect.gen(function* () {
          yield* emitEffect({
            type: 'progress',
            phase:
              batch.startPage === batch.endPage
                ? `Rendering page ${batch.startPage}`
                : `Rendering pages ${batch.startPage}–${batch.endPage}`,
          });
          for (let page = batch.startPage; page <= batch.endPage; page += 1) {
            const html = yield* unstructuredPageHtmlEffect(page, pageElements(batch, page), [
              options.apiKey,
            ]).pipe(Effect.mapError((error) => error.cause));
            yield* emitEffect({ type: 'replace', target: `#page-${page}`, html });
          }
        }),
      ),
  };

  const streamEffect = options.streamPdf
    ? Effect.tryPromise({
        try: (signal) =>
          options.streamPdf!({
            ...streamOptions,
            signal,
            fetchImpl: options.fetchImpl,
          } satisfies StreamUnstructuredPdfOptions),
        catch: (cause) => sessionFailure('stream', cause),
      })
    : (options.streamPdfEffect ?? streamUnstructuredPdfEffect)(streamOptions).pipe(
        Effect.provideService(FetchHttpClient.Fetch, options.fetchImpl ?? fetch.bind(globalThis)),
        Effect.provideService(HttpClient.TracerPropagationEnabled, false),
        Effect.provide(FetchHttpClient.layer),
      );

  const coreEffect = Effect.suspend(() => {
    if (closed || snapshot.status === 'done' || snapshot.status === 'error') return Effect.void;
    snapshot = { ...snapshot, status: 'running' };
    return Effect.gen(function* () {
      yield* emitEffect({ type: 'progress', phase: 'Reading PDF' });
      yield* streamEffect;
      yield* emitEffect({ type: 'done' });
    });
  });

  const reportFailureEffect = (error: unknown) =>
    Effect.gen(function* () {
      yield* Effect.sync(() => {
        try {
          options.onFailure?.(error);
        } catch {
          // Diagnostic output is outside the live contract and cannot suppress its terminal event.
        }
      });
      yield* emitEffect({ type: 'error', message: safePublicFailure(error) });
    });

  const runEffect = coreEffect.pipe(
    Effect.catchCause((cause) => {
      const squashed = Cause.squash(cause);
      const providerError =
        squashed instanceof LiveViewSessionError ? sessionErrorCauses.get(squashed) : squashed;
      const error = controller.signal.aborted
        ? controller.signal.reason
        : promiseCompatibleProviderFailure(providerError);
      return reportFailureEffect(error);
    }),
    Effect.onInterrupt(() =>
      reportFailureEffect(controller.signal.reason).pipe(Effect.catch(() => Effect.void)),
    ),
  );

  const startRun = (): Promise<void> => {
    runFiber ??= Effect.runFork(runEffect);
    return Effect.runPromise(Fiber.join(runFiber)).catch((error: unknown) => {
      if (closed || controller.signal.aborted) return;
      throw error;
    });
  };

  return {
    id,
    title: options.title,
    snapshot: () => ({
      ...snapshot,
      blocks: snapshot.blocks.map((block) => ({ ...block })),
    }),
    events: (after = 0) => log.filter((event) => event.seq > after).map((event) => ({ ...event })),
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    start() {
      if (closed) return startPromise ?? Promise.resolve();
      startPromise ??= startRun();
      return startPromise;
    },
    done() {
      if (closed) return startPromise ?? Promise.resolve();
      startPromise ??= startRun();
      return startPromise;
    },
    async close() {
      if (closed) return;
      closed = true;
      controller.abort();
      if (runFiber) {
        await Effect.runPromise(Fiber.interrupt(runFiber)).catch(() => undefined);
        await startPromise?.catch(() => undefined);
      } else {
        await Effect.runPromise(
          emitEffect({ type: 'error', message: 'Conversion was cancelled.' }),
        ).catch(() => undefined);
      }
    },
  };
}
